import React from "react";

import { Col, Form, Row } from "react-bootstrap";

import { Control, Controller, FieldErrors } from "react-hook-form";

import { ParticipantTypes } from "@api/graphql";
import CountryDropdown from "@components/form/CountryDropdown";
import { FormErrorText } from "@components/form/FormErrorText";
import { FormHelperText } from "@components/form/FormHelperText";
import { mandatoryFields } from "@pages/register/hooks/errorResolver";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { isReporter } from "@util/index";

import styles from "./index.module.scss";

interface BusinessTaxDetailsFormProps {
  control: Control<UserDetailsInput>;
  errors: FieldErrors<UserDetailsInput>;
  isJoiningExistingAccount: boolean;
  isRoyaltyRecipientOnly: boolean;
  abnMessage?: string;
  isInternalUser?: boolean;
  isEditing?: boolean;
  isArtist: boolean;
  isBeneficiary: boolean;
  isArtCentre: boolean;
  isCMO: boolean;
  isAMP: boolean;
  accountStatus?: string;
  participantTypes?: ParticipantTypes[];
}

export const BusinessTaxDetailsForm: React.FC<BusinessTaxDetailsFormProps> = ({
  control,
  errors,
  isJoiningExistingAccount,
  isRoyaltyRecipientOnly,
  abnMessage,
  isInternalUser,
  isEditing,
  isArtist,
  isBeneficiary,
  isCMO,
  isAMP,
  isArtCentre,
  accountStatus,
  participantTypes,
}) => {
  const showCitizenshipFields = !isJoiningExistingAccount && isRoyaltyRecipientOnly;
  const isReporterRole = isReporter(isAMP, isCMO, isArtCentre);
  const residentForTaxDisabled = isCMO; // for both internal creates CMO account or user registers CMO account
  return (
    <div className={styles["form-section"]}>
      {!isJoiningExistingAccount && (
        <Row>
          <Col md={12} sm={12}>
            <Form.Group>
              <Form.Label>
                {mandatoryFields.taxDetails.companyTradingName({
                  isArtist,
                  isBeneficiary,
                  isJoiningExistingAccount,
                  accountStatus,
                  participantTypes,
                }) && "* "}
                Company / Trading name
              </Form.Label>
              <Controller
                name="tax_details.company_trading_name"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Enter your company"
                    isInvalid={!!errors?.tax_details?.company_trading_name}
                  />
                )}
              />
              <FormErrorText error={errors?.tax_details?.company_trading_name} />
            </Form.Group>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={12} sm={12}>
          <Form.Group>
            <Form.Label>
              {mandatoryFields.taxDetails.abnAcn({
                isAmp: isAMP,
                isArtCenter: isArtCentre,
                accountStatus,
                participantTypes,
              }) && "* "}
              Registered ABN
            </Form.Label>
            <Controller
              name="tax_details.abn_acn"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="number"
                  placeholder="Enter your ABN"
                  isInvalid={!!errors?.tax_details?.abn_acn}
                  onWheel={(event) => event.currentTarget.blur()}
                  disabled={isEditing && !isInternalUser}
                />
              )}
            />
            {abnMessage && <FormHelperText>{abnMessage}</FormHelperText>}
            <FormErrorText error={errors?.tax_details?.abn_acn} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12} sm={12}>
          <Form.Group>
            <Form.Label>
              {mandatoryFields.taxDetails.legalEntityName({
                isReporter: isReporterRole,
                accountStatus,
                participantTypes,
              }) && "* "}
              Legal entity name
            </Form.Label>
            <Controller
              name="tax_details.legal_entity_name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your legal entity name"
                  className={isEditing && !isInternalUser ? styles["disabled-field"] : ""}
                  disabled={isEditing && !isInternalUser}
                  isInvalid={!!errors?.tax_details?.legal_entity_name}
                />
              )}
            />
            <FormErrorText error={errors?.tax_details?.legal_entity_name} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12} sm={12}>
          <Form.Group>
            <Controller
              name="tax_details.is_aus_resident"
              control={control}
              render={({ field }) => (
                <Form.Check
                  className={styles["form-check"]}
                  type="checkbox"
                  label="Are you an Australian resident for tax purposes?"
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                  isInvalid={!!errors?.tax_details?.is_aus_resident}
                  disabled={residentForTaxDisabled}
                />
              )}
            />
            <FormErrorText error={errors?.tax_details?.is_aus_resident} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12} sm={12}>
          <Form.Group>
            <Form.Label>
              {(showCitizenshipFields ||
                mandatoryFields.taxDetails.residencyCitizenship({
                  accountStatus,
                  participantTypes,
                })) &&
                "* "}
              Residency / Citizenship
            </Form.Label>
            <CountryDropdown
              fieldName="tax_details.residency_citizenship"
              control={control}
              isRequired={showCitizenshipFields}
              isDisabled={false}
              isMultiSelect
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};
